import { supabase } from "./supabase";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

async function authFetch(path: string, options: RequestInit = {}) {
	const {
		data: { session },
	} = await supabase.auth.getSession();
	const token = session?.access_token;

	const res = await fetch(`${API_BASE}${path}`, {
		...options,
		headers: {
			"Content-Type": "application/json",
			...(token ? { Authorization: `Bearer ${token}` } : {}),
			...options.headers,
		},
	});

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(body.detail || `API error ${res.status}`);
	}

	if (res.status === 204) return null;
	return res.json();
}

export interface Rating {
	id: string;
	user_id: string;
	drink_name: string;
	brand: string | null;
	category: "energy" | "soda" | "sparkling";
	energy_level: number;
	flavor_score: number;
	vibe_tags: string[];
	tasting_notes: string | null;
	created_at: string;
	updated_at: string;
}

export interface RatingCreate {
	drink_name: string;
	brand?: string;
	category: "energy" | "soda" | "sparkling";
	energy_level: number;
	flavor_score: number;
	vibe_tags: string[];
	tasting_notes?: string;
}

export interface Stats {
	total_ratings: number;
	average_flavor_score: number | null;
	top_flavor: { drink_name: string; flavor_score: number } | null;
	average_energy_level: number | null;
	categories: { energy: number; soda: number; sparkling: number } | null;
	top_vibe_tags: string[];
}

export interface RatingListResponse {
	data: Rating[];
	total: number;
}

export function fetchStats(): Promise<Stats> {
	return authFetch("/stats");
}

export function fetchRatings(params?: {
	category?: string;
	search?: string;
	limit?: number;
	offset?: number;
}): Promise<RatingListResponse> {
	const searchParams = new URLSearchParams();
	if (params?.category) searchParams.set("category", params.category);
	if (params?.search) searchParams.set("search", params.search);
	if (params?.limit) searchParams.set("limit", String(params.limit));
	if (params?.offset) searchParams.set("offset", String(params.offset));
	const qs = searchParams.toString();
	return authFetch(`/ratings${qs ? `?${qs}` : ""}`);
}

export function fetchRating(id: string): Promise<Rating> {
	return authFetch(`/ratings/${id}`);
}

export function createRating(data: RatingCreate): Promise<Rating> {
	return authFetch("/ratings", {
		method: "POST",
		body: JSON.stringify(data),
	});
}

export function removeRating(id: string): Promise<null> {
	return authFetch(`/ratings/${id}`, { method: "DELETE" });
}
